import { useEffect, useRef } from "react";

import { LogoutReason } from "@/constants/Login";
import { BrowserAuthStatus } from "@/hooks/browser/browserAuthStatus";
import useAppStartupBrowserAuthSession from "@/hooks/browser/useAppStartupBrowserAuthSession";
import useHandleAuthExpiration from "@/hooks/browser/useHandleAuthExpiration";
import useAuth from "@/hooks/useAuth";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import { useAppStartupDispatch } from "@/store/reducers/appStartup";

interface UseWebAuthenticationProps {
  onAuthFullyInitialized: () => void;
}

const useWebAuthFlow = ({
  onAuthFullyInitialized,
}: UseWebAuthenticationProps) => {
  const { logout } = useAuth();
  const { setCanLoggedInUserSeeAppContent } = useAppStartupDispatch();
  const hasInitializedRef = useRef<boolean>(false);
  const { browserAuthStatus, isBrowserAuthLoading } =
    useAppStartupBrowserAuthSession();
  const { isMyEquipV2LoginEnabled } = useFeatureFlags();

  useHandleAuthExpiration();

  const onAuthFullyInitializedRef = useRef(onAuthFullyInitialized);
  useEffect(() => {
    onAuthFullyInitializedRef.current = onAuthFullyInitialized;
  }, [onAuthFullyInitialized]);

  useEffect(() => {
    if (isMyEquipV2LoginEnabled) return;

    // if FF is off, this app should not serve auth'd routes;
    // instead immediately re-route to my-equip-v1
    // login page's logout() invocation will detect the logoutReason and redirect to my-equip-v1
    void logout(LogoutReason.NotAcceptingLogins);

    // mark root navigator ready; otherwise app/(login)/index.web.tsx would not proceed with logout
    onAuthFullyInitializedRef.current();
  }, [isMyEquipV2LoginEnabled, logout]);

  useEffect(() => {
    // only initialize at app startup; if already have, don't re-initialize
    if (hasInitializedRef.current) return;

    // if FF is off, let the above useEffect handle routing, and don't do any routing here
    if (!isMyEquipV2LoginEnabled) return;

    // browserAuthSession loads auth0 SDK state. Necessary to handle the flow of
    // App -> redirect to auth0.com/login -> redirect back to App.
    if (isBrowserAuthLoading) return;

    if (browserAuthStatus === BrowserAuthStatus.LoggedIn) {
      onAuthFullyInitialized();
      hasInitializedRef.current = true;
      return;
    }

    // both "expired" and "logged-out" route to login; always set a logoutReason explicitly
    const logoutReason =
      browserAuthStatus === BrowserAuthStatus.Expired
        ? LogoutReason.AuthSessionExpiredAtStartup
        : LogoutReason.JustPreLoginCleanup;
    void logout(logoutReason);
    onAuthFullyInitialized();
    hasInitializedRef.current = true;
  }, [
    browserAuthStatus,
    isBrowserAuthLoading,
    isMyEquipV2LoginEnabled,
    logout,
    onAuthFullyInitialized,
    setCanLoggedInUserSeeAppContent,
  ]);
};

export default useWebAuthFlow;
